/**
 * **Woraus die Erzeugung besteht** — die Kachel „Erzeugung" nach dem Entwurf
 * (`ErzeugungCard` in `ui_kits/dashboard/StromErzeugungScreen.jsx`): die große
 * Zahl ist PV plus Akku, darunter die Energie je Quelle, oben rechts die Leistung
 * je Quelle, neben der Beschriftung die Leistung zusammen, als Unterkante ein
 * Anteilsbalken.
 *
 * **Zwei Lagen, und die Zahlen kommen je Lage von woanders:**
 *
 * - **Es gibt keinen Speicher:** PV ist die Erzeugung der Bilanz (`totals`), die
 *   Leistung aus den Momentanwerten der Zähler (`liveNow`).
 * - **Der Speicher steht hinter dem Erzeugungszähler** — seit #40 die einzige Lage,
 *   einen eigenen Speicherzähler im Messkonzept gibt es nicht mehr (HM-133): Der Zähler misst,
 *   was PV und Akku zusammen abgeben — das ist die große Zahl. PV brutto und
 *   Entladung kennen nur die Geräte der Erzeugungsanlagen (`behindGeneration`), die
 *   Leistung `LiveValuesResponse.behindGeneration`. **Beginnen die Geräte erst im
 *   Zeitraum, ist die Aufteilung eine Teilsumme und sagt das** (`partial`): Ein
 *   Gerät, das erst seit heute meldet, lieferte für Woche, Monat und Jahr dieselben
 *   Zahlen wie für heute — so stand die Kachel nach 0.50.0 auf jedem Zeitraum gleich,
 *   ohne dass man es ihr ansah. Bis 0.56.5 fiel die Aufteilung deshalb ganz weg; an
 *   einer neuen Anlage blieb damit der ganze erste Monat leer, und ein Gerätewechsel
 *   leerte den Tag. Jetzt steht die Zahl da und daneben „ab dem ersten Wert im
 *   Zeitraum" (Auftraggeber, 2026-10-07).
 *
 * **Die große Zahl ist PV plus Entladung, wie im Entwurf** (Auftraggeber,
 * 2026-10-02, HM-138): Sie sagt, was die Erzeugungsseite des Hauses geliefert hat
 * — aus den Modulen und aus dem Akku. Gespeicherter PV-Strom steht darin zweimal,
 * einmal beim Erzeugen und einmal beim Entladen; das ist die Lesart dieser Kachel
 * und nicht die der Bilanz, die bei „Verwendung" und im Messkonzept bleibt.
 * Fehlt ein Summand, ist die Summe unbekannt (Regel 8).
 */
import type { ApiComponents } from "@hausmeter/sdk";
import type { LiveNow } from "./live-now.js";

type Energy = Pick<ApiComponents["schemas"]["SiteEnergyResponse"], "totals" | "behindGeneration">;
type BehindNow = ApiComponents["schemas"]["LiveValuesResponse"]["behindGeneration"];

/** Unter diesem Betrag ruht eine Batterie — Rauschen eines Wechselrichters ist kein Laden. */
export const BATTERY_IDLE_W = 50;

export interface GenerationSplit {
  /** `bilance`: aus den Zählern des Messkonzepts. `devices`: aus den Geräten hinter dem Zähler. */
  readonly source: "bilance" | "devices";
  /** Die Aufteilung beginnt erst im Zeitraum — gezählt ab dem ersten Wert eines Geräts. */
  readonly partial: boolean;
  /** Ob es einen Akku gibt — ohne ihn keine zweite Quelle und kein Balken. */
  readonly hasBattery: boolean;
  readonly pvWh: number | null;
  /** Aus dem Speicher entladen, in Wh — `null` ohne Akku oder unbekannt. */
  readonly batteryWh: number | null;
  /** In den Speicher geladen, in Wh — `null` ohne Akku oder unbekannt (HM-156). */
  readonly batteryChargedWh: number | null;
  /** PV plus Entladung — `null`, wenn ein Summand fehlt. */
  readonly totalWh: number | null;
  /** Anteil der PV an PV + Entladung, 0…1 — `null`, wenn ein Summand fehlt oder beide null sind. */
  readonly pvShare: number | null;
  /** PV jetzt, in W, nie negativ. */
  readonly nowPvW: number | null;
  /** Akku jetzt, in W, mit Vorzeichen: positiv lädt. */
  readonly nowBatteryW: number | null;
}

export function generationSplit(
  energy: Energy,
  now: LiveNow,
  behindNow: BehindNow | null | undefined,
): GenerationSplit | null {
  const generation = energy.totals.find((t) => t.quantityKey === "generation");
  if (generation === undefined) return null;

  const behind = energy.behindGeneration;
  if (behind?.storage != null) {
    const pvWh = behind.pv?.energyWh ?? null;
    const batteryWh = behind.storage.dischargedWh;
    const batteryChargedWh = behind.storage.chargedWh;
    const live = behindNow?.hasBattery === true ? behindNow : null;
    return {
      source: "devices",
      partial: behind.partial,
      hasBattery: true,
      pvWh,
      batteryWh,
      batteryChargedWh,
      totalWh: generation.value,
      pvShare: shareOf(pvWh, batteryWh),
      nowPvW: live?.pvW ?? null,
      nowBatteryW: live?.batteryW ?? null,
    };
  }

  return withTotals({
    source: "bilance",
    partial: false,
    hasBattery: false,
    pvWh: generation.value,
    batteryWh: null,
    batteryChargedWh: null,
    nowPvW: now.generation ?? null,
    nowBatteryW: null,
  });
}

function withTotals(
  split: Omit<GenerationSplit, "totalWh" | "pvShare">,
): GenerationSplit {
  if (!split.hasBattery) return { ...split, totalWh: split.pvWh, pvShare: null };
  return {
    ...split,
    totalWh: split.pvWh === null || split.batteryWh === null ? null : split.pvWh + split.batteryWh,
    pvShare: shareOf(split.pvWh, split.batteryWh),
  };
}

function shareOf(pvWh: number | null, batteryWh: number | null): number | null {
  if (pvWh === null || batteryWh === null) return null;
  const pv = Math.max(pvWh, 0);
  const battery = Math.max(batteryWh, 0);
  return pv + battery <= 0 ? null : pv / (pv + battery);
}
